import { useEffect, useId, useRef, type ReactNode } from 'react'
import { Link } from 'react-router'
import { errorMessage } from '../api'
import { AUTO_REPLY_WARNING, channelPill, type ChannelKind, type ChannelStatus } from '../lib/channels'
import { Icon, type IconName } from './Icon'

/** Small outlined action button used in channel cards (Pencil: 8x12 padding, 12.5px semibold, radius 9). */
export const SMALL_ACTION = 'btn btn-ghost !rounded-[9px] !px-3 !py-2 !text-[12.5px]'

const CHANNEL_MARK: Record<ChannelKind, { bg: string; icon: IconName }> = {
  whatsapp: { bg: 'bg-accent', icon: 'whatsapp' },
  messenger: { bg: 'bg-facebook', icon: 'facebook' },
  instagram: { bg: 'bg-instagram', icon: 'instagram' },
}

/** Round channel badge: WhatsApp, Facebook (Messenger and Page comments) or Instagram. Decorative. */
export function ChannelMark({ channel, size = 18 }: { channel: ChannelKind; size?: number }) {
  const m = CHANNEL_MARK[channel]
  return (
    <span className={`inline-flex shrink-0 items-center justify-center rounded-full text-white ${m.bg}`} style={{ width: size, height: size }} aria-hidden>
      <Icon name={m.icon} size={Math.round(size * 0.56)} strokeWidth={2.2} />
    </span>
  )
}

/** Brand mark: a speech bubble with three typing dots (Pencil "Logo/Mark", 64x64 grid). */
export function LogoMark({ size = 28 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 64 64" aria-hidden focusable="false" className="shrink-0">
      <path className="fill-accent" d="M11 29a24 24 0 1 0 48 0 24 24 0 1 0-48 0z m4.34 13.77l-7.34 17.23 18.79-8.45z" />
      <path fill="#fff" d="M20.7 29a3.8 3.8 0 1 0 7.6 0 3.8 3.8 0 1 0-7.6 0zM31.2 29a3.8 3.8 0 1 0 7.6 0 3.8 3.8 0 1 0-7.6 0zM41.7 29a3.8 3.8 0 1 0 7.6 0 3.8 3.8 0 1 0-7.6 0z" />
    </svg>
  )
}

export function Logo({ to = '/', size = 'md' }: { to?: string; size?: 'md' | 'sm' }) {
  return (
    <Link to={to} className="inline-flex items-center gap-2 rounded-md" aria-label="Lobby, home">
      <LogoMark size={size === 'md' ? 28 : 25} />
      <span className={`font-head font-bold tracking-[-0.4px] ${size === 'md' ? 'text-[19px]' : 'text-[17px]'}`}>Lobby</span>
    </Link>
  )
}

export function Spinner({ size = 16, className = '' }: { size?: number; className?: string }) {
  return <Icon name="loader" size={size} className={`spin ${className}`} />
}

export function Loading({ label = 'Loading…' }: { label?: string }) {
  return (
    <div role="status" className="flex items-center gap-3 py-10 text-sm text-muted">
      <Spinner />
      {label}
    </div>
  )
}

type Tone = 'info' | 'success' | 'warn' | 'error'
const TONE: Record<Tone, { box: string; icon: IconName }> = {
  info: { box: 'bg-surface border-border text-ink', icon: 'sparkles' },
  success: { box: 'bg-accent-soft border-transparent text-accent', icon: 'circle-check' },
  warn: { box: 'bg-bubble-owner border-transparent text-owner-ink', icon: 'alert-triangle' },
  error: { box: 'bg-danger-soft border-transparent text-danger', icon: 'alert-triangle' },
}

export function Banner({
  tone = 'info',
  title,
  children,
  action,
  icon,
}: {
  tone?: Tone
  title?: ReactNode
  children?: ReactNode
  action?: ReactNode
  icon?: IconName
}) {
  const t = TONE[tone]
  return (
    <div
      role={tone === 'error' ? 'alert' : 'status'}
      className={`flex flex-col gap-3 rounded-[12px] border px-4 py-3 text-[13.5px] sm:flex-row sm:items-center ${t.box}`}
    >
      <div className="flex flex-1 items-start gap-2.5">
        <Icon name={icon ?? t.icon} size={16} className="mt-[2px] shrink-0" />
        <div className="flex-1">
          {title && <p className="font-semibold">{title}</p>}
          {children && <div className={title ? 'mt-0.5 opacity-90' : ''}>{children}</div>}
        </div>
      </div>
      {action && <div className="shrink-0">{action}</div>}
    </div>
  )
}

export function ErrorState({ error, onRetry, title = "This didn't load" }: { error: unknown; onRetry?: () => void; title?: string }) {
  return (
    <Banner
      tone="error"
      title={title}
      action={
        onRetry && (
          <button type="button" className="btn btn-sm btn-danger" onClick={onRetry}>
            <Icon name="refresh" size={14} /> Try again
          </button>
        )
      }
    >
      {errorMessage(error)}
    </Banner>
  )
}

export function EmptyState({ icon, title, children, action }: { icon: IconName; title: string; children?: ReactNode; action?: ReactNode }) {
  return (
    <div className="flex flex-col items-center gap-3 rounded-[14px] border border-dashed border-border bg-surface px-6 py-10 text-center">
      <span className="flex h-10 w-10 items-center justify-center rounded-[12px] bg-sand text-muted">
        <Icon name={icon} size={18} />
      </span>
      <p className="font-head text-[17px] font-semibold">{title}</p>
      {children && <div className="max-w-[440px] text-sm leading-[22px] text-muted">{children}</div>}
      {action}
    </div>
  )
}

/** "Applies to all channels" chip under page titles whose settings are shared by WhatsApp, Facebook and Instagram. */
export function AllChannelsChip() {
  return (
    <span className="inline-flex items-center gap-1.5 self-start rounded-full border border-border bg-surface px-2.5 py-1 text-[12px] font-medium">
      <span className="h-[7px] w-[7px] rounded-full bg-accent" aria-hidden />
      <span className="h-[7px] w-[7px] rounded-full bg-facebook" aria-hidden />
      <span className="h-[7px] w-[7px] rounded-full bg-instagram" aria-hidden />
      Applies to all channels
    </span>
  )
}

export function PageHeader({
  title,
  subtitle,
  actions,
  allChannels = false,
}: {
  title: string
  subtitle?: ReactNode
  actions?: ReactNode
  /** Shows the "Applies to all channels" chip below the subtitle. */
  allChannels?: boolean
}) {
  return (
    <header className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
      <div className="flex flex-col gap-1">
        <h1 className="font-head text-[26px] font-semibold tracking-[-0.6px] sm:text-[28px]">{title}</h1>
        {subtitle && <p className="text-sm text-muted">{subtitle}</p>}
        {allChannels && (
          <div className="pt-1.5">
            <AllChannelsChip />
          </div>
        )}
      </div>
      {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
    </header>
  )
}

export function StatusPill({ tone, children, pulse }: { tone: 'green' | 'amber' | 'grey' | 'red'; children: ReactNode; pulse?: boolean }) {
  const cls = {
    green: 'bg-accent-soft text-accent',
    amber: 'bg-bubble-owner text-owner-ink',
    grey: 'bg-sand text-muted',
    red: 'bg-danger-soft text-danger',
  }[tone]
  const dot = { green: 'bg-accent', amber: 'bg-owner-ink', grey: 'bg-muted', red: 'bg-danger' }[tone]
  return (
    <span className={`chip ${cls}`}>
      <span className={`h-[7px] w-[7px] rounded-full ${dot} ${pulse ? 'pulse' : ''}`} aria-hidden />
      {children}
    </span>
  )
}

export function ChannelStatePill({ channel }: { channel: ChannelStatus }) {
  const p = channelPill(channel)
  return <StatusPill tone={p.tone}>{p.label}</StatusPill>
}

export function ProgressBar({ value, max, label, className = 'w-40' }: { value: number; max: number; label: string; className?: string }) {
  const pct = max > 0 ? Math.round((Math.min(value, max) / max) * 100) : 0
  return (
    <div
      role="progressbar"
      aria-label={label}
      aria-valuemin={0}
      aria-valuemax={max}
      aria-valuenow={value}
      className={`h-1.5 overflow-hidden rounded-full bg-sand ${className}`}
    >
      <div className="h-full rounded-full bg-accent transition-[width] duration-500" style={{ width: `${pct}%` }} />
    </div>
  )
}

/** Label + control + hint + error, wired with ids for screen readers. */
export function Field({
  label,
  hint,
  error,
  counter,
  aside,
  children,
}: {
  label: ReactNode
  hint?: ReactNode
  error?: string | null
  counter?: ReactNode
  aside?: ReactNode
  children: (ids: { id: string; describedBy: string | undefined; invalid: boolean }) => ReactNode
}) {
  const id = useId()
  const hintId = `${id}-hint`
  const errId = `${id}-err`
  const describedBy = [hint ? hintId : null, error ? errId : null].filter(Boolean).join(' ') || undefined
  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-center justify-between gap-2">
        <label htmlFor={id} className="label">
          {label}
        </label>
        {aside}
      </div>
      {children({ id, describedBy, invalid: !!error })}
      <div className="flex items-start justify-between gap-3">
        <div className="flex flex-col gap-0.5">
          {hint && (
            <p id={hintId} className="hint">
              {hint}
            </p>
          )}
          {error && (
            <p id={errId} className="text-[12.5px] font-medium text-danger">
              {error}
            </p>
          )}
        </div>
        {counter}
      </div>
    </div>
  )
}

export function CharCount({ value, max }: { value: number; max: number }) {
  const over = value > max
  return (
    <span className={`shrink-0 text-[12px] tabular-nums ${over ? 'font-semibold text-danger' : 'text-muted'}`} aria-live={over ? 'polite' : 'off'}>
      {value.toLocaleString('en-GB')} / {max.toLocaleString('en-GB')}
    </span>
  )
}

export function Avatar({ text, tone = 'sand', size = 32 }: { text: string; tone?: 'sand' | 'accent'; size?: number }) {
  return (
    <span
      aria-hidden
      className={`flex shrink-0 items-center justify-center rounded-full text-[11px] font-bold ${
        tone === 'accent' ? 'bg-accent text-white' : 'bg-sand text-muted'
      }`}
      style={{ width: size, height: size }}
    >
      {text}
    </span>
  )
}

/**
 * WAI-ARIA tabs: arrow keys, Home and End move between tabs (automatic activation).
 * Render the active panel with `tabPanelProps(idBase, value)`.
 */
export function Tabs<T extends string>({
  idBase,
  label,
  tabs,
  value,
  onChange,
}: {
  idBase: string
  label: string
  tabs: { value: T; label: ReactNode }[]
  value: T
  onChange: (v: T) => void
}) {
  const move = (from: number, delta: number | 'home' | 'end') => {
    const n = tabs.length
    const i = delta === 'home' ? 0 : delta === 'end' ? n - 1 : (from + delta + n) % n
    const next = tabs[i]
    if (!next) return
    onChange(next.value)
    document.getElementById(`${idBase}-tab-${next.value}`)?.focus()
  }
  return (
    <div role="tablist" aria-label={label} className="flex max-w-full gap-1 overflow-x-auto border-b border-border">
      {tabs.map((t, i) => {
        const selected = t.value === value
        return (
          <button
            key={t.value}
            id={`${idBase}-tab-${t.value}`}
            type="button"
            role="tab"
            aria-selected={selected}
            aria-controls={`${idBase}-panel-${t.value}`}
            tabIndex={selected ? 0 : -1}
            onClick={() => onChange(t.value)}
            onKeyDown={(e) => {
              if (e.key === 'ArrowRight') move(i, 1)
              else if (e.key === 'ArrowLeft') move(i, -1)
              else if (e.key === 'Home') move(i, 'home')
              else if (e.key === 'End') move(i, 'end')
              else return
              e.preventDefault()
            }}
            className={`-mb-px inline-flex shrink-0 items-center gap-1.5 border-b-2 px-3 py-2.5 text-[13.5px] font-semibold ${
              selected ? 'border-accent text-ink' : 'border-transparent text-muted hover:text-ink'
            }`}
          >
            {t.label}
          </button>
        )
      })}
    </div>
  )
}

export const tabPanelProps = (idBase: string, value: string) => ({
  id: `${idBase}-panel-${value}`,
  role: 'tabpanel' as const,
  'aria-labelledby': `${idBase}-tab-${value}`,
  tabIndex: 0,
})

/**
 * Modal confirmation on the native <dialog> (focus trap, Escape and inert background come from the browser).
 * Escape or Cancel calls `onCancel`.
 */
export function ConfirmDialog({
  open,
  title,
  children,
  confirmLabel,
  busy,
  onConfirm,
  onCancel,
}: {
  open: boolean
  title: string
  children: ReactNode
  confirmLabel: string
  busy?: boolean
  onConfirm: () => void
  onCancel: () => void
}) {
  const ref = useRef<HTMLDialogElement>(null)
  const titleId = useId()
  useEffect(() => {
    const d = ref.current
    if (!d) return
    if (open && !d.open) d.showModal()
    else if (!open && d.open) d.close()
  }, [open])
  return (
    <dialog
      ref={ref}
      aria-labelledby={titleId}
      onCancel={(e) => {
        e.preventDefault()
        onCancel()
      }}
      className="m-auto w-[calc(100%-32px)] max-w-[440px] rounded-[16px] border border-border bg-surface p-0 text-ink shadow-xl"
    >
      {open && (
        <div className="flex flex-col gap-4 p-5 sm:p-6">
          <h2 id={titleId} className="font-head text-[18px] font-semibold">
            {title}
          </h2>
          <div className="text-[14px] leading-[21px] text-muted">{children}</div>
          <div className="flex flex-wrap justify-end gap-2">
            <button type="button" className="btn btn-ghost" onClick={onCancel} autoFocus>
              Cancel
            </button>
            <button type="button" className="btn btn-danger" onClick={onConfirm} disabled={busy}>
              {busy ? <Spinner size={14} /> : null}
              {confirmLabel}
            </button>
          </div>
        </div>
      )}
    </dialog>
  )
}

/** Meta's own automatic replies must be off, or contacts get two answers. */
export function AutoReplyWarning() {
  return (
    <div className="flex items-start gap-3 rounded-[12px] bg-bubble-owner p-3.5 text-[13px] leading-[20px] text-owner-ink">
      <Icon name="alert-triangle" size={16} className="mt-0.5 shrink-0" />
      <p>{AUTO_REPLY_WARNING}</p>
    </div>
  )
}

